import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { SUPPORT_PALETTE, SUPPORT_MENU_PALETTE as PALETTE } from "./supportPalette";
import { MENU_ENTER_CLASS } from "../components/motion/useModalMotion";

// Above this many *visible* tools, the menu grows a filter search at the top.
const SEARCH_THRESHOLD = 8;

// The trigger button lives inline in the dark SupportHeader bar, so it reuses
// SUPPORT_PALETTE's action-button tokens (supportPalette.js) and its
// hover/focus-visible rule (".support-header-control", defined in
// SupportHeader's scoped style block). Item hover/focus color and the search
// input's focus ring reuse the ".support-tools-menu-item" /
// ".support-tools-search" rules defined there too.

// Support-only "Tools" dropdown for the masquerade header. Renders through a
// portal (same pattern as UserMenu) so it isn't clipped by header overflow.
export function SupportToolsMenu({ tools, ctx }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const openedWithKeyboardRef = useRef(false);
  // Keyboard navigation moves focus to the active item; mouse hover only
  // highlights it, so hovering never pulls focus out of the search input.
  const focusActiveItemRef = useRef(true);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const itemRefs = useRef([]);

  const visibleTools = useMemo(
    () => tools.filter((t) => (t.isVisible ? t.isVisible(ctx) : true)),
    [tools, ctx],
  );

  const showSearch = visibleTools.length > SEARCH_THRESHOLD;

  const filteredTools = useMemo(() => {
    if (!showSearch || !query.trim()) return visibleTools;
    const q = query.trim().toLowerCase();
    return visibleTools.filter((t) => t.label.toLowerCase().includes(q));
  }, [visibleTools, showSearch, query]);

  const groups = useMemo(() => {
    const map = new Map();
    filteredTools.forEach((t) => {
      if (!map.has(t.group)) map.set(t.group, []);
      map.get(t.group).push(t);
    });
    return Array.from(map.entries());
  }, [filteredTools]);

  const close = useCallback((focusButton) => {
    setOpen(false);
    setQuery("");
    setActiveIndex(-1);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  const selectItem = useCallback((tool) => {
    tool.onSelect(ctx);
    close(false);
  }, [ctx, close]);

  useEffect(() => {
    if (!open) return undefined;
    function handleClickOutside(e) {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inButton && !inMenu) close(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, close]);

  // On open, focus the search box (if shown). Opened with the keyboard, the first
  // item becomes active; opened with the mouse, no item is highlighted (the pink
  // active color would otherwise read as a hover) and focus goes to the menu itself
  // so ArrowDown still reaches the first item.
  useEffect(() => {
    if (!open) return;
    if (showSearch) {
      searchRef.current?.focus();
    } else if (openedWithKeyboardRef.current && filteredTools.length > 0) {
      setActiveIndex(0);
    } else {
      menuRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showSearch]);

  useEffect(() => {
    if (open && activeIndex >= 0 && focusActiveItemRef.current && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].focus();
    }
  }, [activeIndex, open]);

  const openMenu = (withKeyboard = false) => {
    openedWithKeyboardRef.current = withKeyboard;
    focusActiveItemRef.current = true;
    setOpen(true);
  };

  const handleButtonKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      openMenu(true);
    }
  };

  const moveActive = (delta) => {
    if (filteredTools.length === 0) return;
    setActiveIndex((prev) => {
      let next = prev + delta;
      if (next < 0) next = filteredTools.length - 1;
      if (next >= filteredTools.length) next = 0;
      return next;
    });
  };

  const handleMenuKeyDown = (e) => {
    focusActiveItemRef.current = true;
    if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      if (filteredTools.length) setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      if (filteredTools.length) setActiveIndex(filteredTools.length - 1);
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  const menuId = "support-tools-menu";
  const buttonId = "support-tools-menu-button";
  const rect = open && buttonRef.current ? buttonRef.current.getBoundingClientRect() : null;

  let itemIndex = -1;

  return (
    <>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="support-header-control"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={handleButtonKeyDown}
        style={{
          display: "flex", alignItems: "center", gap: 4,
          background: SUPPORT_PALETTE.actionBg, border: SUPPORT_PALETTE.actionBorder, borderRadius: 6,
          color: SUPPORT_PALETTE.actionText, fontSize: 12, fontWeight: 600, padding: "6px 12px", cursor: "pointer",
        }}
      >
        Tools
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && rect && ReactDOM.createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          className={MENU_ENTER_CLASS}
          tabIndex={-1}
          aria-labelledby={buttonId}
          onKeyDown={handleMenuKeyDown}
          style={{
            position: "fixed",
            transformOrigin: "top right",
            top: rect.bottom + 6,
            right: Math.max(0, window.innerWidth - rect.right),
            background: PALETTE.panelBg,
            border: PALETTE.panelBorder,
            borderRadius: 10,
            minWidth: 220,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            zIndex: 400,
            padding: 6,
            maxHeight: 360,
            overflowY: "auto",
          }}
        >
          {showSearch && (
            <input
              ref={searchRef}
              type="text"
              className="support-tools-search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(-1); }}
              placeholder="Search tools…"
              aria-label="Search tools"
              style={{
                width: "100%", boxSizing: "border-box", background: PALETTE.inputBg,
                border: PALETTE.inputBorder, borderRadius: 6, padding: "6px 8px",
                color: PALETTE.itemText, fontSize: 12, marginBottom: 6, outline: "none",
              }}
            />
          )}
          {groups.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 12, color: PALETTE.muted }}>No tools found</div>
          )}
          {groups.map(([groupName, groupTools]) => (
            <div key={groupName} role="group" aria-label={groupName} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: PALETTE.muted, padding: "4px 10px" }}>
                {groupName}
              </div>
              {groupTools.map((tool) => {
                itemIndex += 1;
                const idx = itemIndex;
                return (
                  <button
                    key={tool.id}
                    // React 19 ref cleanup: each item clears its own slot on
                    // unmount, so render never has to mutate the ref.
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                      return () => { itemRefs.current[idx] = null; };
                    }}
                    type="button"
                    role="menuitem"
                    className="support-tools-menu-item"
                    tabIndex={activeIndex === idx ? 0 : -1}
                    onClick={() => selectItem(tool)}
                    onMouseEnter={() => {
                      focusActiveItemRef.current = false;
                      setActiveIndex(idx);
                    }}
                    onMouseLeave={() => setActiveIndex((current) => (current === idx ? -1 : current))}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      background: "transparent",
                      border: "none", borderRadius: 6,
                      // Driven by activeIndex (set by both mouse and keyboard) instead of a CSS
                      // :hover rule, which this inline color would otherwise override.
                      color: activeIndex === idx ? SUPPORT_PALETTE.accentText : PALETTE.itemText,
                      transition: "color 120ms ease-out",
                      fontSize: 13, padding: "8px 10px", cursor: "pointer",
                    }}
                  >
                    {tool.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
